import type {
  ConnectionTypeValue,
  ContactReferenceTypeValue,
  FilterOption,
} from '@/api/address-book/types';
import { ConnectionType, ContactReferenceType } from '@/api/address-book/types';

export const ContactTypeGroup = {
  Individual: 'Individual',
  Organization: 'Organization',
} as const;

export type ContactTypeGroupValue = (typeof ContactTypeGroup)[keyof typeof ContactTypeGroup];

export const ConnectionStatus = {
  Connected: 'Connected',
  Auto: 'Auto',
  NotOnGamma: 'NotOnGamma',
} as const;

export type ConnectionStatusValue = (typeof ConnectionStatus)[keyof typeof ConnectionStatus];

const CONTACT_TYPE_GROUP_MEMBERS: Record<ContactTypeGroupValue, ContactReferenceTypeValue[]> = {
  [ContactTypeGroup.Individual]: [ContactReferenceType.Individual, ContactReferenceType.External],
  [ContactTypeGroup.Organization]: [ContactReferenceType.Entity, ContactReferenceType.Organization],
};

const CONNECTION_STATUS_MEMBERS: Record<ConnectionStatusValue, ConnectionTypeValue[]> = {
  [ConnectionStatus.Connected]: [ConnectionType.Direct, ConnectionType.Team],
  [ConnectionStatus.Auto]: [ConnectionType.Auto],
  [ConnectionStatus.NotOnGamma]: [ConnectionType.Manual],
};

const CONNECTION_STATUS_LABELS: Record<ConnectionStatusValue, string> = {
  [ConnectionStatus.Connected]: 'Connected',
  [ConnectionStatus.Auto]: 'Auto',
  [ConnectionStatus.NotOnGamma]: 'Not on Gamma',
};

export const CONTACT_TYPE_GROUPS: ContactTypeGroupValue[] = Object.values(ContactTypeGroup);

export const CONNECTION_STATUSES: ConnectionStatusValue[] = Object.values(ConnectionStatus);

const isContactTypeGroup = (value: string): value is ContactTypeGroupValue =>
  value in CONTACT_TYPE_GROUP_MEMBERS;

const isConnectionStatus = (value: string): value is ConnectionStatusValue =>
  value in CONNECTION_STATUS_MEMBERS;

const findGroup = <G extends string>(
  members: Record<G, readonly string[]>,
  groups: readonly G[],
  raw: string | null | undefined,
): G | null => (raw ? (groups.find((group) => members[group].includes(raw)) ?? null) : null);

export const toContactTypeGroup = (
  referenceType: string | null | undefined,
): ContactTypeGroupValue | null =>
  findGroup(CONTACT_TYPE_GROUP_MEMBERS, CONTACT_TYPE_GROUPS, referenceType);

export const toConnectionStatus = (
  type: string | null | undefined,
): ConnectionStatusValue | null => findGroup(CONNECTION_STATUS_MEMBERS, CONNECTION_STATUSES, type);

export const connectionStatusLabel = (status: ConnectionStatusValue): string =>
  CONNECTION_STATUS_LABELS[status];

export const contactTypeLabel = (referenceType: string): string =>
  toContactTypeGroup(referenceType) ?? referenceType;

export const connectionTypeLabel = (type: string): string => {
  const status = toConnectionStatus(type);

  return status ? connectionStatusLabel(status) : type;
};

export const expandContactTypeGroup = (group: ContactTypeGroupValue): ContactReferenceTypeValue[] =>
  CONTACT_TYPE_GROUP_MEMBERS[group];

export const expandConnectionStatus = (status: ConnectionStatusValue): ConnectionTypeValue[] =>
  CONNECTION_STATUS_MEMBERS[status];

const isRawReferenceType = (value: string): value is ContactReferenceTypeValue =>
  (Object.values(ContactReferenceType) as string[]).includes(value);

const isRawConnectionType = (value: string): value is ConnectionTypeValue =>
  (Object.values(ConnectionType) as string[]).includes(value);

export const resolveContactTypeGroup = (value: string): ContactTypeGroupValue | null => {
  if (isContactTypeGroup(value)) return value;
  if (isRawReferenceType(value)) return toContactTypeGroup(value);

  return null;
};

export const resolveConnectionStatus = (value: string): ConnectionStatusValue | null => {
  if (isConnectionStatus(value)) return value;
  if (isRawConnectionType(value)) return toConnectionStatus(value);

  return null;
};

export const collapseReferenceTypeOptions = (options: FilterOption[]): FilterOption[] => {
  const present = new Set(options.map(({ value }) => toContactTypeGroup(value)));

  return CONTACT_TYPE_GROUPS.filter((group) => present.has(group)).map((group) => ({
    value: group,
    label: group,
  }));
};

export const collapseConnectionTypeOptions = (options: FilterOption[]): FilterOption[] => {
  const present = new Set(options.map(({ value }) => toConnectionStatus(value)));

  return CONNECTION_STATUSES.filter((status) => present.has(status)).map((status) => ({
    value: status,
    label: connectionStatusLabel(status),
  }));
};
